/**
 * HR System — Global Currency Formatter
 * 
 * HOW TO USE IN main.js:
 *   import './currency.js'
 * 
 * This registers:
 *   1. Vue.filter('currency')     — use in templates: {{ value | currency }}
 *   2. Vue.mixin formatCurrency   — use in templates: {{ formatCurrency(value) }}
 *   3. Auto-formats GridView columns whose names match known money fields
 */

import Vue from 'vue';

// ── Core formatter ────────────────────────────────────────────
// Returns "1,234.56" for any numeric value. Returns "-" for null/empty.
function formatMoney(value) {
  if (value === null || value === undefined || value === '' || value === '-') return '-';
  const num = parseFloat(value);
  if (isNaN(num)) return value; // not a number — leave as-is (e.g. codes, descriptions)
  return num.toLocaleString('en-MY', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// ── Column names that should always be formatted as currency ──
const MONEY_COLUMNS = new Set([
  // Generic
  'amount', 'amount1', 'amount2', 'amountVariance', 'totalamount', 'subTotal',
  'adjamount', 'adjustamt', 'affectedamt', 'diffamt', 'newamt', 'oldamt',
  'finalamount', 'genamount',

  // Payroll
  'basicsal', 'newbasicsal', 'basicamt',
  'grosspay', 'afgrosspay',
  'netpay',   'afnetpay',
  'aprvamt',  'aprvqty',
  'topay',

  // Tax / Statutory
  'taxgross', 'taxrate', 'loectax',
  'epfpaid',
  'zakatamt', 'zakatp',
  'thpamt',

  // Claims / Allowances
  'allocationamount', 'allocatemtd', 'allocation',
  'exemptionamount',
  'hrdfamtapproved', 'hrdfamtclaimed',
  'maxamt', 'rebatelimitamount', 'rebate',

  // Payroll run specific
  'rate',
]);

// ── Vue filter (use anywhere: {{ val | currency }}) ───────────
Vue.filter('currency', formatMoney);

// ── Global mixin (adds formatCurrency() to every component) ──
Vue.mixin({
  methods: {
    formatCurrency(value) {
      return formatMoney(value);
    },

    // Pass your tabHeaders array — returns new headers with formatters attached
    // Usage in GridView: this.applyMoneyFormatters(this.parentData.tabHeaders)
    applyMoneyFormatters(headers) {
      if (!headers) return headers;
      return headers.map(h => {
        if (MONEY_COLUMNS.has(h.value)) {
          return {
            ...h,
            // Vuetify uses this to render cell content
            formatter: (val) => formatMoney(val),
          };
        }
        return h;
      });
    },
  },
});

export { formatMoney, MONEY_COLUMNS };
